import React, { useMemo } from 'react';

import { chain, groupBy } from 'lodash';
import { FormattedHTMLMessage, FormattedMessage } from 'react-intl';

import { RenewalStatus } from '@appTypes/enums';
import { S, Spin } from '@modules/shared/components';

import {
  useOrgPositions,
  useOrgUnitRosterAdults,
  useUnitRosterAdults,
} from '../duck/hooks';
import { RosterAdultMemberWitRegistration } from '../types';
import styles from './assignees.less';
import { PositionSelector } from './components/PositionSelector';

const activeRegistrations = [
  RenewalStatus.Current,
  RenewalStatus.Eligible,
  RenewalStatus.Renewed,
] as string[];

export const Assignees = () => {
  const { isFetching: isLoadingUnitAdults, data: unitAdultsData } =
    useUnitRosterAdults();
  const {
    isFetching: isLoadingUnitRegistrationAdults,
    data: unitRegistrationAdultsData,
  } = useOrgUnitRosterAdults();
  const { isFetching: isLoadingOrgPositions, data: orgPositionsData } =
    useOrgPositions();
  const isLoading =
    isLoadingUnitAdults ||
    isLoadingOrgPositions ||
    isLoadingUnitRegistrationAdults;

  const { functional, registered } = useMemo(() => {
    const positions = orgPositionsData?.Positions;

    if (positions?.length) {
      const filteredPositions = positions.filter(
        pos => !pos.isYouthPosition && !(pos.min === 0 && pos.max === 0),
      );
      const { true: functional = [], false: registered = [] } = chain(
        filteredPositions,
      )
        .orderBy(['max'], ['asc'])
        .groupBy('isFunctional')
        .value();
      return { functional, registered };
    }

    return { functional: [], registered: [] };
  }, [orgPositionsData]);

  const { key3Positions, programPositions } = useMemo(() => {
    if (registered.length) {
      const { true: key3Positions = [], false: programPositions = [] } =
        groupBy(registered, 'isKey3');
      return { key3Positions, programPositions };
    }

    return { key3Positions: [], programPositions: [] };
  }, [registered]);

  const unitUsers: RosterAdultMemberWitRegistration[] | undefined =
    useMemo(() => {
      const users = unitAdultsData?.users;
      if (!users) return undefined;
      if (!unitRegistrationAdultsData)
        return users as RosterAdultMemberWitRegistration[];

      return users.map(user => {
        const foundRegistration = unitRegistrationAdultsData.members.find(
          item => item.personGuid === user.personGuid,
        );

        if (foundRegistration) {
          const renewalStatus =
            foundRegistration.registrarInfo?.renewalStatus || '';
          const registrationExpireDt =
            foundRegistration.registrarInfo?.registrationExpireDt || '';

          return {
            ...user,
            hasValidRegistration: activeRegistrations.includes(renewalStatus),
            renewalStatus,
            registrationExpireDt,
          };
        }

        return {
          ...user,
          hasValidRegistration: false,
          renewalStatus: RenewalStatus.NeedsToRegister,
        };
      }) as RosterAdultMemberWitRegistration[];
    }, [unitAdultsData, unitRegistrationAdultsData]);

  const positionsWithMoreUsersThanMax = useMemo(() => {
    if (!unitUsers?.length) return [];
    const allValidPositions = [...functional, ...registered];

    return allValidPositions.filter(pos => {
      if (pos.personsAssigned.length > pos.max) return false;

      const peopleWithValidRegistration = pos.personsAssigned.filter(person => {
        const foundPerson = unitUsers.find(
          item => item.personGuid === person.personGuid,
        );

        return foundPerson?.hasValidRegistration;
      });

      return peopleWithValidRegistration.length > pos.max;
    });
  }, [functional, registered, unitUsers]);

  return (
    <Spin delay={100} size="large" spinning={isLoadingOrgPositions}>
      <S size="3" colored hr className={styles.sectionTitle}>
        <FormattedMessage id="permissionManager.assignees.sectionTitle.registered" />
      </S>
      <S size="6" className={styles.lapsedNote}>
        <FormattedHTMLMessage id="permissionManager.assignees.lapsedNote" />
      </S>
      <S size="4" colored className={styles.sectionSubtitle}>
        <FormattedMessage id="permissionManager.assignees.sectionTitle.key3" />
      </S>
      <div className={styles.sectionContainer}>
        {key3Positions.map(pos => (
          <PositionSelector
            key={pos.PositionId}
            positionData={pos}
            unitUsers={unitUsers}
            loading={isLoading}
            positionsWithMoreUsersThanMax={positionsWithMoreUsersThanMax}
            registeredPositions={registered}
          />
        ))}
      </div>
      <S size="4" colored className={styles.sectionSubtitle}>
        <FormattedMessage id="permissionManager.assignees.sectionTitle.programAdminis" />
      </S>
      <div className={styles.sectionContainer}>
        {programPositions.map(pos => (
          <PositionSelector
            key={pos.PositionId}
            positionData={pos}
            unitUsers={unitUsers}
            positionsWithMoreUsersThanMax={positionsWithMoreUsersThanMax}
            registeredPositions={registered}
          />
        ))}
      </div>
      <S size="3" colored hr className={styles.sectionTitle}>
        <FormattedMessage id="permissionManager.assignees.sectionTitle.functional" />
      </S>
      <S size="6" className={styles.lapsedNote}>
        <FormattedHTMLMessage id="permissionManager.assignees.lapsedNote" />
      </S>
      <div className={styles.sectionContainer}>
        {functional.map(pos => (
          <PositionSelector
            key={pos.PositionId}
            positionData={pos}
            unitUsers={unitUsers}
            positionsWithMoreUsersThanMax={positionsWithMoreUsersThanMax}
            registeredPositions={registered}
            functionalPositions={functional}
          />
        ))}
      </div>
    </Spin>
  );
};
